import { useState } from 'react';

import { useAuth } from '../../../hooks/useAuth';
import { useFirebase } from '../../../hooks/useFirebase';

import { queryDelete, queryGet, queryPost, queryPut } from '../../tools/tools';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useEndpoint = (endpoint, key) => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const queryKey = [key, user?.uid];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKey,
    queryFn: async () => await queryGet(remoteConfig, endpoint),
    enabled: !!user?.accessToken && !!remoteConfig
  });

  const wrapMutate =
    (mutationFn, type) =>
    ({ data, onSuccess, onError, onComplete }) => {
      setIsMutating(true);
      setMutationError(null);
      mutationFn(data, {
        onSuccess: async (res) => {
          onSuccess?.(res, type);
          onComplete?.(null, res, type);
          await refetch();
        },
        onError: (err) => {
          setMutationError(err);
          onError?.(err, type);
          onComplete?.(err, null, type);
        },
        onSettled: () => {
          setIsMutating(false);
        }
      });
    };

  const {
    mutate: _create,
    isLoading: isCreateLoading,
    isError: isCreateError,
    reset: resetCreate
  } = useMutation({
    mutationFn: async (item) => await queryPost(remoteConfig, endpoint, item)
  });

  const {
    mutate: _update,
    isLoading: isUpdateLoading,
    isError: isUpdateError,
    reset: resetUpdate
  } = useMutation({
    mutationFn: async (item) => await queryPut(remoteConfig, endpoint, item)
  });

  const {
    mutate: _remove,
    isLoading: isDeleteLoading,
    isError: isDeleteError,
    reset: resetDelete
  } = useMutation({
    mutationFn: async (id) => await queryDelete(remoteConfig, endpoint, id)
  });

  const reset = () => {
    resetCreate();
    resetUpdate();
    resetDelete();
  };

  const getById = async (id, onSuccess) => {
    try {
      setIsMutating(true);
      setMutationError(null);
      const result = await queryGet(remoteConfig, `${endpoint}/${id}`);

      queryClient.setQueryData([key, user?.uid], (old) => {
        if (!old) return [result];
        const existingIndex = old.findIndex((item) => item.id === result.id);
        if (existingIndex >= 0) {
          const updated = [...old];
          updated[existingIndex] = result;
          return updated;
        } else {
          return [...old, result];
        }
      });

      onSuccess?.(result);
      return result;
    } catch (err) {
      setMutationError(err);
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  return {
    data,
    loading: isLoading,
    error: isError,
    create: wrapMutate(_create, 'create'),
    update: wrapMutate(_update, 'update'),
    remove: wrapMutate(_remove, 'delete'),
    getById,
    reset,
    refetch,
    mutating: isCreateLoading || isUpdateLoading || isDeleteLoading || isMutating,
    mutatingError: isCreateError || isUpdateError || isDeleteError || mutationError
  };
};
